import { AppSearchSelect } from "../../components/appComponents";
import DatePickerWithRange from "../../components/misc/date-picker-with-range";
import { useEffect, useState, useMemo } from "react";
import common from "../../helper/common";
import { DateminFormat, respEnum } from "../../constants/appConstant";
import AdvanceTable from "../../components/advanceTable";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import Chart from "react-apexcharts";
import { useThemeStore } from "../../store";
import { useTheme } from "../../hooks";
import { themes } from "../../config/thems";
import { getGridConfig } from "../../helper/appex-chart-options";
import {
  Users,
  Calendar,
  DollarSign,
  Clock,
  TrendingUp,
  Activity,
  Smartphone,
  Building,
  Globe,
  MessageCircle,
  Trophy,
  MapPin,
  BarChart3,
  Target,
  Landmark,
} from "lucide-react";

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    summary: null,
    daily: [],
    tournaments: null,
    categories: null,
    cityClubs: [],
    newSignups: [],
  });
  const [clubId, setClubId] = useState(null);
  const [accountList, setAccountList] = useState([]);
  const [From, setFrom] = useState();
  const [To, setTo] = useState();

  // Theme hooks
  const { theme: config } = useThemeStore();
  const { theme: mode } = useTheme();
  const theme = themes.find((theme) => theme.name === config);

  useEffect(() => {
    if (clubId == null) {
      getBookingSummary({});
      return;
    } else {
      fetchAllData();
    }
  }, [clubId]);

  useEffect(() => {
    getClubAccount();
  }, []);

  const fetchAllData = async () => {
    await getBookingSummary(From, To, clubId);
  };

  const getClubAccount = async () => {
    return new Promise(async (res) => {
      const response = await common.ApiService.getInstance().request(
        "GetClubsDDL"
      );
      const data = [{ id: null, name: "Select" }, ...(response?.data ?? [])];

      setAccountList(data);

      setClubId(null);
    });
  };

  const getBookingSummary = async (From, To, clubId) => {
    try {
      let response;
      if ((!From || !To) && clubId) {
        response = await common.ApiService.getInstance().request(
          `GetFullSummary?clubId=${clubId}`,
          "GET"
        );
      } else if (From && To && clubId) {
        response = await common.ApiService.getInstance().request(
          `GetFullSummary?startDateTime=${From}&tillDate=${To}&clubId=${clubId}`,
          "GET"
        );
      } else if (From && To) {
        response = await common.ApiService.getInstance().request(
          `GetFullSummary?startDateTime=${From}&tillDate=${To}`,
          "GET"
        );
      } else {
        response = await common.ApiService.getInstance().request(
          `GetFullSummary`,
          "GET"
        );
      }

      if (response.status == respEnum.Success && response.data) {
        // Process the new API structure
        const summary = response.data.find(
          (item) => item.data_type === "summary"
        );
        const dailyData = response.data.find(
          (item) => item.data_type === "daily"
        );
        const tournaments = response.data.find(
          (item) => item.data_type === "tournament"
        );
        const categories = response.data.find(
          (item) => item.data_type === "tournament_category"
        );
        const cityClubsData = response.data.find(
          (item) => item.data_type === "city_clubs"
        );
        const signupData = response.data.find(
          (item) => item.data_type === "daily_signup"
        );

        // Extract daily data from the nested structure
        let daily = [];
        if (dailyData?.dailyData) {
          daily = dailyData.dailyData;
        }

        const cityClubs = cityClubsData?.city_club_Data || [];
        // if (cityClubsData?.city_club_Data) {
        // try {
        //   cityClubs = JSON.parse(cityClubsData.city_club_Data);
        // } catch (e) {
        //   console.log("Error parsing city clubs data:", e);
        // }
        // }

        const newSignups = signupData?.daily_signup_data || [];

        setDashboardData({
          summary: summary || null,
          daily: daily || [],
          tournaments: tournaments || null,
          categories: categories || null,
          // cityClubs: cityClubs,
          cityClubs,
          newSignups,
        });
      } else {
        setDashboardData({
          summary: null,
          daily: [],
          tournaments: null,
          categories: null,
          cityClubs: [],
          newSignups: [],
        });
      }
    } catch (error) {
      console.log(error);
      setDashboardData({
        summary: null,
        daily: [],
        tournaments: null,
        categories: null,
        cityClubs: [],
        newSignups: [],
      });
    }
  };

  // Format number for display
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num?.toLocaleString() || "0";
  };

  // Chart configurations
  const platformColors = [
    "#0C1B3A",
    "#2E55B9",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
  ];

  const bookingDistributionChart = useMemo(() => {
    if (!dashboardData.summary) return null;

    return {
      series: [
        dashboardData.summary.AppBookingCount || 0,
        dashboardData.summary.ClubBookingCount || 0,
        dashboardData.summary.MyAppBookingCount || 0,
        dashboardData.summary.WebBookingCount || 0,
        dashboardData.summary.WhatsappBookingCount || 0,
      ],
      options: {
        chart: {
          type: "donut",
          toolbar: { show: false },
        },
        labels: ["App", "Club", "My App", "Web", "WhatsApp"],
        colors: platformColors,
        legend: {
          position: "bottom",
          fontSize: "14px",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total Bookings",
                  fontSize: "16px",
                  color: "#0C1B3A",
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return formatNumber(value) + " bookings";
            },
          },
        },
      },
    };
  }, [dashboardData.summary]);

  const dailyBookingsChart = useMemo(() => {
    if (!dashboardData.daily?.length) return null;

    const sortedDaily = [...dashboardData.daily].sort(
      (a, b) => new Date(a.booking_date) - new Date(b.booking_date)
    );

    return {
      series: [
        {
          name: "Total Bookings",
          data: sortedDaily.map((item) => item.TotalBookingCount || 0),
        },
        {
          name: "App Bookings",
          data: sortedDaily.map((item) => item.AppBookingCount || 0),
        },
        {
          name: "Club Bookings",
          data: sortedDaily.map((item) => item.ClubBookingCount || 0),
        },
        {
          name: "My App Bookings",
          data: sortedDaily.map((item) => item.MyAppBookingCount || 0),
        },
        {
          name: "Web Bookings",
          data: sortedDaily.map((item) => item.WebBookingCount || 0),
        },
        {
          name: "Whatsapp Bookings",
          data: sortedDaily.map((item) => item.WhatsappBookingCount || 0),
        },
      ],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: [
          "#515c90ff",
          "#2a2f3aff",
          "#2E55B9",
          "#22C55E",
          "#F59E0B",
          "#EF4444",
        ],
        xaxis: {
          categories: sortedDaily.map((item) =>
            new Date(item.booking_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          ),
        },
        stroke: {
          width: 3,
          curve: "smooth",
        },
        markers: {
          size: 6,
        },
        grid: {
          borderColor: "#f1f5f9",
        },
      },
    };
  }, [dashboardData.daily]);

  const dailySignupsChart = useMemo(() => {
    if (!dashboardData.newSignups?.length) return null;

    const sortedDaily = [...dashboardData.newSignups].sort(
      (a, b) => new Date(a.signup_date) - new Date(b.signup_date)
    );

    return {
      series: [
        {
          name: "User Signups",
          data: sortedDaily.map(
            (item) => item.GoogleSignups + item.AppleSignups || 0
          ),
        },
      ],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#2E55B9"],
        xaxis: {
          categories: sortedDaily.map((item) =>
            new Date(item.signup_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          ),
        },
        stroke: {
          width: 3,
          curve: "smooth",
        },
        markers: {
          size: 6,
        },
        grid: {
          borderColor: "#f1f5f9",
        },
      },
    };
  }, [dashboardData.newSignups]);

  return (
    <div className="space-y-6 bg-gradient-to-br from-slate-50 to-blue-50/30 min-h-screen -m-6 p-6">
      {/* Enhanced Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        <div className="flex items-center flex-wrap justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0C1B3A] flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] p-3 rounded-xl shadow-lg">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-1 ml-1">
              Real-time analytics and insights
            </p>
          </div>
          <div className="flex items-center gap-4">
            <AppSearchSelect
              value={clubId}
              onChange={(val) => {
                if (val != null) {
                  setClubId(val);
                  setFrom(null);
                  setTo(null);
                } else {
                  setClubId(null);
                  setFrom(null);
                  setTo(null);
                  getBookingSummary();
                }
              }}
              placeholder="Select Club"
              data={accountList}
              className="w-64"
            />

            <DatePickerWithRange
              key={clubId}
              setFromTo={{
                from: From ? new Date(From) : undefined,
                to: To ? new Date(To) : undefined,
              }}
              onDateChanged={(val) => {
                const updatedFrom = val?.from
                  ? common.moment(val.from).format(DateminFormat)
                  : undefined;
                const updatedTo = val?.to
                  ? common.moment(val.to).format(DateminFormat)
                  : undefined;

                setFrom(updatedFrom);
                setTo(updatedTo);
                getBookingSummary(updatedFrom, updatedTo, clubId);
              }}
            />
          </div>
        </div>
      </div>

      {dashboardData.summary ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            <Card className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">
                      Total Bookings
                    </p>
                    <p className="text-4xl font-bold mb-1">
                      {formatNumber(dashboardData.summary.TotalBookingCount)}
                    </p>
                    <p className="text-white/70 text-xs">
                      Across all platforms
                    </p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-600 to-emerald-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">
                      Total Revenue
                    </p>
                    <p className="text-4xl font-bold mb-1">
                      {formatNumber(dashboardData.summary.TotalEarning)}
                    </p>
                    <p className="text-white/70 text-xs">All-time earnings</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <DollarSign className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-600 to-purple-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">
                      App Users
                    </p>
                    <p className="text-4xl font-bold mb-1">
                      {formatNumber(dashboardData.summary.AppUserCount)}
                    </p>
                    <p className="text-white/70 text-xs">Unique players</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-600 to-blue-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">
                      Club Users
                    </p>
                    <p className="text-4xl font-bold mb-1">
                      {formatNumber(dashboardData.summary.ClubUserCount)}
                    </p>
                    <p className="text-white/70 text-xs">Unique players</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-600 to-amber-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">
                      Booking Hours
                    </p>
                    <p className="text-4xl font-bold mb-1">
                      {formatNumber(dashboardData.summary.TotalBookingHours)}
                    </p>
                    <p className="text-white/70 text-xs">Total court time</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Platform Stats - Compact & Modern */}
          <div className="mt-6">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-5">
                <h3 className="text-lg font-bold text-[#0C1B3A] mb-4 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#2E55B9]" />
                  Platform Breakdown
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className="bg-gradient-to-br from-[#0C1B3A]/5 to-[#0C1B3A]/10 rounded-xl p-4 border-l-4 border-[#0C1B3A] hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone className="h-5 w-5 text-[#0C1B3A]" />
                      <p className="text-xs font-semibold text-slate-600 uppercase">
                        App
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-[#0C1B3A]">
                      {formatNumber(dashboardData.summary.AppBookingCount)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(dashboardData.summary.AppBookingHours)} hrs
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-[#2E55B9]/5 to-[#2E55B9]/10 rounded-xl p-4 border-l-4 border-[#2E55B9] hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="h-5 w-5 text-[#2E55B9]" />
                      <p className="text-xs font-semibold text-slate-600 uppercase">
                        Club
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-[#2E55B9]">
                      {formatNumber(dashboardData.summary.ClubBookingCount)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(dashboardData.summary.ClubBookingHours)} hrs
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 rounded-xl p-4 border-l-4 border-emerald-500 hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-emerald-600" />
                      <p className="text-xs font-semibold text-slate-600 uppercase">
                        MyApp
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatNumber(dashboardData.summary.MyAppBookingCount)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(dashboardData.summary.MyAppBookingHours)}{" "}
                      hrs
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-amber-500/5 to-amber-500/10 rounded-xl p-4 border-l-4 border-amber-500 hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="h-5 w-5 text-amber-600" />
                      <p className="text-xs font-semibold text-slate-600 uppercase">
                        Web
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      {formatNumber(dashboardData.summary.WebBookingCount)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(dashboardData.summary.WebBookingHours)} hrs
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 rounded-xl p-4 border-l-4 border-green-500 hover:shadow-md transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="h-5 w-5 text-green-600" />
                      <p className="text-xs font-semibold text-slate-600 uppercase">
                        WhatsApp
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatNumber(dashboardData.summary.WhatsappBookingCount)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatNumber(dashboardData.summary.WhatsappBookingHours)}{" "}
                      hrs
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {bookingDistributionChart && (
              <Card className="shadow-xl border-0 bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-bold text-[#0C1B3A] flex items-center gap-2">
                    <div className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] p-2 rounded-lg">
                      <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                    Booking Distribution by Platform
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-6">
                  <Chart
                    options={bookingDistributionChart.options}
                    series={bookingDistributionChart.series}
                    type="donut"
                    height={320}
                  />
                </CardContent>
              </Card>
            )}

            {dailyBookingsChart && (
              <Card className="shadow-xl border-0 bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-bold text-[#0C1B3A] flex items-center gap-2">
                    <div className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] p-2 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    Daily Booking Trends
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-6">
                  <Chart
                    options={dailyBookingsChart.options}
                    series={dailyBookingsChart.series}
                    type="line"
                    height={320}
                  />
                </CardContent>
              </Card>
            )}

            {dailySignupsChart && (
              <Card className="shadow-xl border-0 bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-bold text-[#0C1B3A] flex items-center gap-2">
                    <div className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] p-2 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    Daily Signup Trends
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-6">
                  <Chart
                    options={dailySignupsChart.options}
                    series={dailySignupsChart.series}
                    type="line"
                    height={320}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {dashboardData.tournaments && (
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Trophy className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Tournaments
                  </h3>
                  <p className="text-3xl font-bold text-indigo-600">
                    {dashboardData.tournaments.tournamentCount || 0}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Active tournaments
                  </p>
                </CardContent>
              </Card>
            )}

            {dashboardData.categories && (
              <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Activity className="h-12 w-12 text-pink-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Categories
                  </h3>
                  <p className="text-3xl font-bold text-pink-600">
                    {dashboardData.categories.categoryCount || 0}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Tournament categories
                  </p>
                </CardContent>
              </Card>
            )}

            {dashboardData.cityClubs?.length > 0 && (
              <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <MapPin className="h-12 w-12 text-teal-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Cities
                  </h3>
                  <p className="text-3xl font-bold text-teal-600">
                    {dashboardData.cityClubs.length}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Cities with{" "}
                    {dashboardData.cityClubs.reduce(
                      (sum, city) => sum + (city.TotalClubs || 0),
                      0
                    )}{" "}
                    clubs
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* City-wise Clubs
          {dashboardData.cityClubs?.length > 0 && (
            <Card className="shadow-xl border-0">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50">
                <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <MapPin className="h-6 w-6 text-blue-600" />
                  City-wise Club Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardData.cityClubs.map((city, index) => (
                    <div
                      key={index}
                      className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-800">
                          {city.city_name}
                        </h4>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                          {city.club_count} clubs
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )} */}

          {/* City Wise Stats - Compact Design */}
          {dashboardData.cityClubs?.length > 0 && (
            <div className="mb-8">
              {/* Section Header */}
              <Card className="border-0 shadow-lg mb-6 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-[#0C1B3A] to-[#2E55B9] p-3 rounded-xl shadow-lg">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-[#0C1B3A]">
                          City-wise Club Statistics
                        </h2>
                        <p className="text-slate-600 text-sm mt-0.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Active clubs with bookings
                          </span>
                          <span className="mx-2">•</span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            Inactive clubs
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-[#0C1B3A]">
                        {dashboardData.cityClubs.length}
                      </p>
                      <p className="text-sm text-slate-600">Cities</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {[...dashboardData.cityClubs]
                  .sort((a, b) => b.TotalClubs - a.TotalClubs)
                  .map((city) => (
                    <Card
                      key={city.CityId}
                      className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden bg-white"
                    >
                      {/* Compact Card Header */}
                      <CardHeader
                        className="pb-2 pt-3 px-4"
                        style={{
                          background: `linear-gradient(135deg, #0C1B3A 0%, #2E55B9 100%)`,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Landmark className="h-5 w-5 text-white flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-bold text-white truncate">
                                {city.CityName}
                              </h3>
                              <p className="text-white/70 text-xs">
                                {city.TotalClubs}{" "}
                                {city.TotalClubs === 1 ? "Club" : "Clubs"}
                              </p>
                            </div>
                          </div>
                          {/* Active/Inactive Compact Badge */}
                          <div className="flex gap-1 items-center ml-2 flex-shrink-0">
                            {city.ActiveClubs > 0 && (
                              <div className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-md flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                                {city.ActiveClubs}
                              </div>
                            )}
                            {city.NotActiveClubs > 0 && (
                              <div className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-md">
                                {city.NotActiveClubs}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardHeader>

                      {/* Compact Card Content */}
                      <CardContent className="p-3">
                        {city.TotalBookings > 0 ? (
                          <>
                            {/* Total Bookings */}
                            <div className="mb-3 text-center bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-lg p-2">
                              <p className="text-2xl font-bold text-[#0C1B3A]">
                                {formatNumber(city.TotalBookings)}
                              </p>
                              <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">
                                Total Bookings
                              </p>
                            </div>

                            {/* Compact Booking Types Grid */}
                            <div className="space-y-1.5">
                              {city.AppBookingCounts > 0 && (
                                <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 rounded-md hover:bg-[#0C1B3A]/5 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <Smartphone className="h-3.5 w-3.5 text-[#0C1B3A]" />
                                    <span className="text-xs font-medium text-slate-700">
                                      App
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold text-[#0C1B3A]">
                                    {formatNumber(city.AppBookingCounts)}
                                  </span>
                                </div>
                              )}

                              {city.ClubBookingCounts > 0 && (
                                <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 rounded-md hover:bg-[#2E55B9]/5 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <Building className="h-3.5 w-3.5 text-[#2E55B9]" />
                                    <span className="text-xs font-medium text-slate-700">
                                      Club
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold text-[#2E55B9]">
                                    {formatNumber(city.ClubBookingCounts)}
                                  </span>
                                </div>
                              )}

                              {city.MyAppBookingCounts > 0 && (
                                <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 rounded-md hover:bg-emerald-500/5 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <Target className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-xs font-medium text-slate-700">
                                      MyApp
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold text-emerald-600">
                                    {formatNumber(city.MyAppBookingCounts)}
                                  </span>
                                </div>
                              )}

                              {city.WebBookingCounts > 0 && (
                                <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 rounded-md hover:bg-amber-500/5 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-3.5 w-3.5 text-amber-600" />
                                    <span className="text-xs font-medium text-slate-700">
                                      Web
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold text-amber-600">
                                    {formatNumber(city.WebBookingCounts)}
                                  </span>
                                </div>
                              )}

                              {city.WhatsAppBookingCounts > 0 && (
                                <div className="flex items-center justify-between bg-slate-50 px-2.5 py-1.5 rounded-md hover:bg-green-500/5 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <MessageCircle className="h-3.5 w-3.5 text-green-600" />
                                    <span className="text-xs font-medium text-slate-700">
                                      WhatsApp
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold text-green-600">
                                    {formatNumber(city.WhatsAppBookingCounts)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <Activity className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-slate-500 font-medium">
                              No bookings yet
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="shadow-xl border-0 bg-white">
          <CardContent className="p-16 text-center">
            <div className="bg-gradient-to-br from-slate-100 to-blue-100/50 rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center">
              <Activity className="h-16 w-16 text-[#2E55B9]" />
            </div>
            <h3 className="text-2xl font-bold text-[#0C1B3A] mb-3">
              No Data Available
            </h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Please select filters or check your data source to view dashboard
              analytics.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
